import { lazy, Suspense, useCallback, useEffect, useMemo, useState } from 'react'
import { QrScannerModal } from './components/QrScannerModal'
import { AddWorkoutModal, type WorkoutDraft } from './components/AddWorkoutModal'
import { WorkoutPickModal } from './components/WorkoutPickModal'
import { HistoryPage } from './components/HistoryPage'
import { FatigueChart } from './components/FatigueChart'
import { ScannerPage } from './components/ScannerPage'
import { SettingsPage } from './components/SettingsPage'
import {
  addWorkout,
  deleteWorkout,
  listWorkouts,
  replaceWorkouts,
  workoutsToStrainEvents,
  type WorkoutRecord,
} from './lib/db'
import { buildLabel } from './lib/buildInfo'
import { formatSetsSummary } from './lib/volume'
import { findMachinesByQr } from './lib/machines'
import type { MeshMuscleId } from './lib/muscles'
import { MESH_LABELS, MESH_TO_MUSCLES, MUSCLES } from './lib/muscles'
import {
  fatigueLabel,
  fatigueHistorySeries,
  hoursUntilFresh,
  muscleFatigue,
} from './lib/recovery'
import type { MachineExercise } from './lib/machines'
import { navigate, routeFromHash, type AppRoute } from './lib/routing'
import { useTheme } from './lib/useTheme'

/** Max workouts shown in the map "Recent" panel. */
export const RECENT_LIMIT = 10

const MuscleBody3D = lazy(() =>
  import('./components/MuscleBody3D').then((m) => ({ default: m.MuscleBody3D })),
)

export default function App() {
  const [workouts, setWorkouts] = useState<WorkoutRecord[]>([])
  const [ready, setReady] = useState(false)
  const [historyReloadToken, setHistoryReloadToken] = useState(0)
  const [route, setRoute] = useState<AppRoute>(() => routeFromHash())
  const [scanOpen, setScanOpen] = useState(false)
  const [addOpen, setAddOpen] = useState(false)
  const [pickMachines, setPickMachines] = useState<MachineExercise[] | null>(null)
  const [draftInitial, setDraftInitial] = useState<
    (Partial<WorkoutDraft> & { machine?: MachineExercise }) | undefined
  >()
  const [selected, setSelected] = useState<MeshMuscleId | null>(null)
  const [toast, setToast] = useState<string | null>(null)
  const [offline, setOffline] = useState(!navigator.onLine)
  const { preference: themePreference, resolved: resolvedTheme, setPreference: setTheme } = useTheme()

  const refresh = useCallback(async () => {
    const rows = await listWorkouts()
    setWorkouts(rows)
    setHistoryReloadToken((n) => n + 1)
    setReady(true)
  }, [])

  useEffect(() => {
    void refresh()
  }, [refresh])

  useEffect(() => {
    const onHash = () => setRoute(routeFromHash())
    window.addEventListener('hashchange', onHash)
    return () => window.removeEventListener('hashchange', onHash)
  }, [])

  useEffect(() => {
    const on = () => setOffline(false)
    const off = () => setOffline(true)
    window.addEventListener('online', on)
    window.addEventListener('offline', off)
    return () => {
      window.removeEventListener('online', on)
      window.removeEventListener('offline', off)
    }
  }, [])

  useEffect(() => {
    if (!toast) return
    const t = window.setTimeout(() => setToast(null), 3200)
    return () => window.clearTimeout(t)
  }, [toast])

  const events = useMemo(() => workoutsToStrainEvents(workouts), [workouts])
  const now = Date.now()

  const saveDraft = async (draft: WorkoutDraft) => {
    await addWorkout({ ...draft, at: Date.now() })
    await refresh()
    setToast(`Logged ${draft.name}`)
  }

  const removeWorkout = async (id: string) => {
    await deleteWorkout(id)
    await refresh()
    setToast('Workout removed')
  }

  const openWorkoutForMachine = useCallback((machine: MachineExercise) => {
    setPickMachines(null)
    setDraftInitial({
      source: 'qr',
      machine,
      machineId: machine.id,
      name: machine.name,
      muscles: machine.muscles,
      qrPayload: machine.qrPayload,
    })
    setAddOpen(true)
  }, [])

  const onScan = useCallback(
    (payload: string) => {
      const matches = findMachinesByQr(payload)
      navigate('map')
      if (matches.length === 0) {
        setToast('QR not recognized yet - add this machine later, or log manually.')
        setDraftInitial({ source: 'qr', qrPayload: payload })
        setAddOpen(true)
        return
      }
      if (matches.length === 1) {
        openWorkoutForMachine(matches[0])
        return
      }
      setAddOpen(false)
      setPickMachines(matches)
    },
    [openWorkoutForMachine],
  )

  const selectedDetail = useMemo(() => {
    if (!selected) return null
    const muscleIds = MESH_TO_MUSCLES[selected]
    if (!muscleIds.length) return null
    const fatigues = muscleIds.map((id) => ({
      id,
      name: MUSCLES[id].name,
      fatigue: muscleFatigue(events, id, now),
      hours: hoursUntilFresh(events, id, now),
    }))
    const avg = fatigues.reduce((s, f) => s + f.fatigue, 0) / fatigues.length
    const series = fatigueHistorySeries(events, muscleIds, { now })
    return { label: MESH_LABELS[selected], avg, fatigues, series }
  }, [selected, events, now])

  const modalKey =
    draftInitial?.machineId ?? draftInitial?.qrPayload ?? (addOpen ? 'manual' : 'closed')

  return (
    <div className="app-shell">
      <a className="skip-link" href="#main">
        Skip to content
      </a>

      <header className="topbar">
        <button type="button" className="brand brand-btn" onClick={() => navigate('map')}>
          <img src="/logos/icon.svg" width={36} height={36} alt="" />
          <div>
            <p className="brand-name">MyoFade</p>
            <p className="brand-sub">{offline ? 'Offline · data on device' : 'Ready · data on device'}</p>
          </div>
        </button>
        <nav className="top-nav" aria-label="Primary">
          <button
            type="button"
            className={route === 'map' ? 'nav-link active' : 'nav-link'}
            aria-current={route === 'map' ? 'page' : undefined}
            onClick={() => navigate('map')}
          >
            Map
          </button>
          <button
            type="button"
            className={route === 'history' ? 'nav-link active' : 'nav-link'}
            aria-current={route === 'history' ? 'page' : undefined}
            onClick={() => navigate('history')}
          >
            History
          </button>
          <button
            type="button"
            className={route === 'settings' ? 'nav-link active' : 'nav-link'}
            aria-current={route === 'settings' ? 'page' : undefined}
            onClick={() => navigate('settings')}
          >
            Settings
          </button>
        </nav>
        <div className="top-actions">
          <button type="button" className="btn-ghost" onClick={() => setScanOpen(true)}>
            Scan QR
          </button>
          <button
            type="button"
            className="btn-primary"
            onClick={() => {
              setDraftInitial(undefined)
              setAddOpen(true)
            }}
          >
            Add workout
          </button>
        </div>
      </header>

      <main
        id="main"
        className={route === 'map' ? 'main-grid' : 'main-history'}
      >
        {route === 'history' ? (
          <HistoryPage
            reloadToken={historyReloadToken}
            onBack={() => navigate('map')}
            onDelete={removeWorkout}
          />
        ) : route === 'scanner' ? (
          <ScannerPage onBack={() => navigate('map')} onToast={setToast} />
        ) : route === 'settings' ? (
          <SettingsPage
            workouts={workouts}
            theme={themePreference}
            onThemeChange={setTheme}
            onBack={() => navigate('map')}
            onReplaceWorkouts={async (rows) => {
              await replaceWorkouts(rows)
              await refresh()
              setToast('Backup restored')
            }}
          />
        ) : (
          <>
            <section className="panel body-panel" aria-labelledby="map-title">
              <div className="panel-head">
                <h1 id="map-title">Muscle map</h1>
                <div className="legend" aria-hidden="true">
                  <span className="swatch grey" /> Ready
                  <span className="swatch red" /> Worked
                </div>
              </div>
              {ready ? (
                <Suspense fallback={<p className="muted pad">Loading 3D map…</p>}>
                  <MuscleBody3D
                    key={resolvedTheme}
                    events={events}
                    selected={selected}
                    onSelect={setSelected}
                    background={resolvedTheme === 'light' ? '#f4f6f8' : '#0c0f12'}
                  />
                </Suspense>
              ) : (
                <p className="muted pad">Loading map…</p>
              )}
            </section>

            <aside className="side">
              <section className="panel" aria-labelledby="detail-title">
                <div className="panel-head">
                  <h2 id="detail-title">Selection</h2>
                  {selected ? (
                    <button type="button" className="btn-ghost" onClick={() => setSelected(null)}>
                      Clear
                    </button>
                  ) : null}
                </div>
                {selectedDetail ? (
                  <div className="detail">
                    <p className="detail-name">{selectedDetail.label}</p>
                    <p>
                      <strong>{fatigueLabel(selectedDetail.avg)}</strong>
                      {' · '}
                      {Math.round(selectedDetail.avg * 100)}% fatigue
                    </p>
                    <FatigueChart label={selectedDetail.label} points={selectedDetail.series} />
                    <ul className="detail-list">
                      {selectedDetail.fatigues.map((f) => (
                        <li key={f.id}>
                          <span>{f.name}</span>
                          <span>
                            {Math.round(f.fatigue * 100)}%
                            {f.hours > 0
                              ? ` · ~${f.hours.toFixed(0)}h to ready`
                              : ' · ready'}
                          </span>
                        </li>
                      ))}
                    </ul>
                  </div>
                ) : (
                  <p className="muted">Tap a muscle on the map for recovery details.</p>
                )}
              </section>

              <section className="panel formula" aria-labelledby="formula-title">
                <h2 id="formula-title">How fade works</h2>
                <p className="muted small">
                  Fatigue = peak × 2^(-hours / half-life). Peak uses set volume (weight × reps) and a
                  relative intensity (first session for an exercise = 10, later sessions scale vs your
                  best prior volume). Quads ~72h half-life; hip abductors ~48h; TFL ~36h. Train-ready
                  under ~20% fatigue (map greys out near ~8%).
                </p>
                <p className="muted small formula-sources">
                  Half-lives are estimates inspired by published recovery windows (~48-72h for hard
                  lower-body work; often shorter for smaller groups) -{' '}
                  <a
                    href="https://pubmed.ncbi.nlm.nih.gov/38689583/"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    Thomas et al., 2024
                  </a>
                  ,{' '}
                  <a
                    href="https://pmc.ncbi.nlm.nih.gov/articles/PMC6719818/"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    Machado et al., 2019
                  </a>
                  . The fade curve, peaks, and readiness cutoffs are a simplified model, not a clinical
                  measure.
                </p>
              </section>
            </aside>

            <section className="panel recent-panel" aria-labelledby="log-title">
              <div className="panel-head">
                <h2 id="log-title">Recent</h2>
                {workouts.length > 0 ? (
                  <button type="button" className="linkish" onClick={() => navigate('history')}>
                    View all
                  </button>
                ) : null}
              </div>
              {workouts.length === 0 ? (
                <p className="muted">No workouts yet. Scan a machine QR or add one manually.</p>
              ) : (
                <ul className="workout-list">
                  {workouts.slice(0, RECENT_LIMIT).map((w) => (
                    <li key={w.id}>
                      <div>
                        <strong>{w.name}</strong>
                        <span className="muted">
                          {new Date(w.at).toLocaleString()} · {formatSetsSummary(w.sets)} ·{' '}
                          {w.intensity}/10
                        </span>
                      </div>
                      <button
                        type="button"
                        className="linkish danger"
                        onClick={() => void removeWorkout(w.id)}
                      >
                        Delete
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </section>
          </>
        )}
      </main>

      <footer className="app-footer">
        <span>MyoFade</span>
        <div className="app-footer-meta">
          <button
            type="button"
            className="app-footer-link"
            aria-current={route === 'scanner' ? 'page' : undefined}
            onClick={() => navigate('scanner')}
          >
            Scanner
          </button>
          <span className="app-footer-build" title="App version and git build">
            Build {buildLabel()}
          </span>
        </div>
      </footer>

      <QrScannerModal open={scanOpen} onClose={() => setScanOpen(false)} onScan={onScan} />
      <WorkoutPickModal
        open={Boolean(pickMachines?.length)}
        machines={pickMachines ?? []}
        onClose={() => setPickMachines(null)}
        onPick={openWorkoutForMachine}
      />
      <AddWorkoutModal
        key={modalKey}
        open={addOpen}
        initial={draftInitial}
        history={workouts}
        onClose={() => setAddOpen(false)}
        onSave={saveDraft}
      />

      {toast ? (
        <div className="toast" role="status" aria-live="polite">
          {toast}
        </div>
      ) : null}
    </div>
  )
}
